import {
    AUTH_CONFIG,
    AuthConfiguration,
    AuthorizationsObject,
    AuthService,
    OAuthProfile,
    UserProfileService,
} from '@dat/angular-auth';
import {
    emptyDatiqUser,
    IDatiqUser,
    IDatiqUserDb,
    IDatiqUserModals,
} from '../models';
import { Observable, of } from 'rxjs';
import { filter, map, mergeMap, tap } from 'rxjs/operators';

import { DatDbClientService } from '@dat/db';
import { DatiqUserMapperService } from './datiq-user-mapper.service';
import { inject, Injectable } from '@angular/core';
import { DatiqUserDbService } from '../store/services/datiq-user-db';
import { DatiqDatadogRumService } from '@dat/datiq-observability';

@Injectable({
    providedIn: 'root',
})
export class DatiqUserObservablesService {
    private _authConfig: AuthConfiguration =
        inject<AuthConfiguration>(AUTH_CONFIG);
    private _dd: DatiqDatadogRumService = inject(DatiqDatadogRumService)

    constructor(
        private readonly _auth: AuthService,
        private readonly _userProfile: UserProfileService,
        private readonly _db: DatDbClientService,
        private readonly _mapper: DatiqUserMapperService,
        private readonly _userDb: DatiqUserDbService,
    ) {}

    logInUser$(): Observable<IDatiqUser> {
        return this.isLoggedIn$().pipe(
            filter((isLoggedIn: boolean): boolean => !isLoggedIn),
            mergeMap(() => this.getLogInUserAuthorizations$()),
            mergeMap((authorizations: AuthorizationsObject) =>
                this.getLoginProfile$().pipe(
                    map(
                        (
                            oAuthProfile: OAuthProfile,
                        ): {
                            oAuthProfile: OAuthProfile;
                            authorizations: AuthorizationsObject;
                        } => ({
                            oAuthProfile,
                            authorizations,
                        }),
                    ),
                ),
            ),
            mergeMap(
                ({
                    oAuthProfile,
                    authorizations,
                }: {
                    oAuthProfile: OAuthProfile;
                    authorizations: AuthorizationsObject;
                }): Observable<IDatiqUser> =>
                    this.getUserModalsFromDB$().pipe(
                        map((modals: IDatiqUserModals) =>
                            this._mapper.mapResponsesToUser(
                                oAuthProfile,
                                authorizations,
                                modals,
                            ),
                        ),
                    ),
            ),
            //TODO: Figure out what is using this doc and move it to the new strategy
            mergeMap(
                (authUser: IDatiqUser): Observable<IDatiqUser> =>
                    this.getUserFromDB$(authUser.id).pipe(
                        map(
                            (dbUser: IDatiqUser): IDatiqUser => ({
                                ...authUser,
                                ...dbUser,
                                loaded: true,
                            }),
                        ),
                    ),
            ),
            // josh.newsom - This is a temporary hotfix to log users out if they haven't logged in since April 2,
            // in order to mitigate issues with users with invalid JWTs due to incident 987
            tap(
                (user: IDatiqUser) => {
                    const authFixDate = new Date(Date.UTC(2025, 3, 2, 6)) // 12AM on April 2, Mountain Time (6AM UTC)
                    const lastLoginDate = new Date(user?.oAuthProfile?.updatedAt) || new Date();
                    if (lastLoginDate.valueOf() < authFixDate.valueOf() ) {
                        this._dd.addAction('forcedUserLogout', {lastLoginDate, user})
                        this.logOutUser$().subscribe();
                    }
                }
            )
        );
    }

    getUserFromDB$(id: string): Observable<IDatiqUser> {
        return !!id
            ? this._db.hasDoc(id).pipe(
                  mergeMap((hasUser: boolean) =>
                      hasUser
                          ? (this._db.getDoc(id) as Observable<IDatiqUserDb>)
                          : (this._db.setDoc(id, {
                                ...emptyDatiqUser,
                                id,
                            }) as Observable<IDatiqUserDb>),
                  ),
                  map((res: IDatiqUserDb) => res.doc),
              )
            : of({ ...emptyDatiqUser });
    }

    isLoggedIn$(): Observable<boolean> {
        return this._auth.loggedIn$;
    }

    getLogInUserAuthorizations$(): Observable<AuthorizationsObject> {
        return this._auth.userAuthorizationsResult$;
    }

    logOutUser$(): Observable<unknown> {

        /**
         * This is a workaround for the Auth0 logout issue.
         * The reason for this is that the AuthService's log out also clears 
         * out a behavior subject is authenticated. This emit causes issues because 
         * the AuthGuard has a side effect that causes the user to log back in. 
         * 
         * We should in the future we should discuss how we can fix this without 
         * the log out side effects.
         */
        this._auth.auth0.logout({
            returnTo: window.location.origin,
            clientID: this._authConfig.oAuth2Config.clientId,
        });

        return of({});
    }

    getLoginProfile$(): Observable<OAuthProfile> {
        return this._userProfile.userProfile$;
    }

    getUserModalsFromDB$(): Observable<IDatiqUserModals> {
        return this._userDb.getModalDoc$();
    }
}
